import { useState, useEffect, useRef } from "react";
import { animate } from "framer-motion";
import { toast } from "sonner";
import { Download, ArrowRight } from "lucide-react";
import { downloadCalcPdf, requestQuote } from "@/lib/calcExport";
import { scrollToId } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const MAX_BASE = 2300;
export const MIN_WAGE = 620.2;
export const TAX_RATE = 0.1;

export const fmt = (v) => v.toLocaleString("bg-BG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const controls = animate(prev.current, value, { duration: 0.5, ease: "easeOut", onUpdate: (v) => setDisplay(v) });
    prev.current = value;
    return () => controls.stop();
  }, [value]);
  return <span>{fmt(display)}</span>;
};

export const Euro = ({ value }) => (
  <>
    <AnimatedNumber value={value} /> €
  </>
);

export const eur = (v) => ({ value: <Euro value={v} />, text: `${fmt(v)} €` });
export const txt = (s) => ({ value: <>{s}</>, text: s });

export const Toggle = ({ options, value, onChange, testidPrefix }) => (
  <div className="flex border border-line mb-8" role="tablist">
    {options.map((t) => (
      <button
        key={t.id}
        role="tab"
        aria-selected={value === t.id}
        data-testid={`${testidPrefix}-${t.id}`}
        onClick={() => onChange(t.id)}
        className={`flex-1 px-4 py-3 text-sm tracking-wide transition-colors duration-300 ${
          value === t.id ? "bg-gold text-ink" : "text-dim hover:text-mist"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

export const Field = ({ id, label, value, onChange, type = "number", testid }) => (
  <div>
    <label className="block font-mono text-xs uppercase tracking-[0.25em] text-dim mb-3" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      data-testid={testid}
      type={type}
      min={type === "number" ? "0" : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-coal border border-line text-mist font-mono text-2xl px-5 py-4 outline-none focus:border-gold transition-colors duration-300 [color-scheme:dark]"
    />
  </div>
);

export const Note = ({ children }) => <p className="text-dim/60 text-xs mt-6 leading-relaxed">{children}</p>;

export const InputCard = ({ kicker, children }) => (
  <div className="border border-line bg-ink p-8 md:p-10 h-full">
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-dim mb-8">{kicker}</p>
    {children}
  </div>
);

export const ResultCard = ({ label, main, mainText, rows, title, service, note = "" }) => {
  const [busy, setBusy] = useState(false);
  const { t, lang } = useLang();
  const payload = { title, resultLabel: label, mainText, rows, note, service, meta: t.calc.pdfMeta, quoteMsg: t.calc.quoteMsg, lang };

  const onPdf = async () => {
    setBusy(true);
    try {
      await downloadCalcPdf(payload);
      toast.success(t.calc.pdfOk);
    } catch {
      toast.error(t.calc.pdfErr);
    } finally {
      setBusy(false);
    }
  };

  const onQuote = () => {
    requestQuote(payload);
    scrollToId("#contact");
  };

  return (
    <div className="border border-gold/40 bg-gold/[0.04] p-8 md:p-10 h-full flex flex-col">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-dim mb-2">{label}</p>
      <p data-testid="calc-result-main" className="font-mono text-5xl md:text-6xl text-gold mb-10">
        {main}
      </p>
      <div className="flex-1 flex flex-col divide-y divide-line">
        {rows.map((row) => (
          <div key={row.testid} data-testid={row.testid} className="flex items-baseline justify-between gap-4 py-4">
            <span className="text-dim text-sm">{row.label}</span>
            <span className="font-mono text-mist text-lg whitespace-nowrap">{row.value}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-line">
        <button
          type="button"
          data-testid="calc-download-pdf"
          onClick={onPdf}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-gold/60 text-gold px-5 py-3 text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
        >
          <Download size={15} />
          {busy ? t.calc.generating : t.calc.pdf}
        </button>
        <button
          type="button"
          data-testid="calc-request-quote"
          onClick={onQuote}
          className="group inline-flex items-center gap-2 bg-gold text-ink px-5 py-3 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300"
        >
          {t.calc.quote}
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
};
